import z, { ZodType } from "zod";

import {
  dayjs,
  IncidentWorkItemStatusSchema,
  looseUuid,
  type EMHAParticipantUnbranded,
} from "@thalamos/common";

import { EmailSchema } from "./email.js";
import { UserIdSchema, type UserIdUnbranded } from "./user.js";

export type IncidentFormIdUnbranded = string;

export const IncidentFormIdSchema =
  looseUuid().brand<"vision-common:IncidentFormId">() satisfies ZodType<IncidentFormIdUnbranded>;

export type IncidentFormId = z.infer<typeof IncidentFormIdSchema>;

/**
 *
 * Incident Work Item
 *
 */

/**
 * An ID for an Incident Work Item
 */
export type IncidentWorkItemIdUnbranded = string;
export const IncidentWorkItemIdSchema =
  looseUuid().brand<"vision-common:IncidentWorkItemId">() satisfies ZodType<IncidentWorkItemIdUnbranded>;
export type IncidentWorkItemId = z.infer<typeof IncidentWorkItemIdSchema>;

/**
 * An Incident Work Item
 */
export type IncidentWorkItemUnbranded = {
  id: IncidentWorkItemIdUnbranded;
  status: string;
  workflowVersion: string;
  shareCode: string;
  pdfUri: string | null;
  createdAt: Date;
  updatedAt: Date;
  isTrainingIncident: boolean;
  anonymisationJobId: string | null;
  anonymiserVersion: string | null;
  version: number;
};

export type IncidentWorkItemEnhancedUnbranded = IncidentWorkItemUnbranded;

export const IncidentWorkItemBaseSchema = z.object({
  id: IncidentWorkItemIdSchema,
  status: IncidentWorkItemStatusSchema,
  workflowVersion: z.string().max(255),
  shareCode: z.union([z.string().regex(/^[A-Z0-9]{8}$/), looseUuid()]),
  pdfUri: z.string().nullable(),
  aspireFrontendUrl: z.url().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isTrainingIncident: z.boolean(),
  anonymisationJobId: z.string().max(255).nullable(),
  anonymiserVersion: z.string().max(255).nullable(),
  version: z.number().int().positive(),
});

export const IncidentWorkItemSchema =
  IncidentWorkItemBaseSchema.brand<"vision-common:IncidentWorkItem">();

export type IncidentWorkItem =
  z.infer<typeof IncidentWorkItemSchema> extends IncidentWorkItemUnbranded
    ? z.infer<typeof IncidentWorkItemSchema>
    : never;

// This is required to strip the 'brand' from the outer type whilst preserving on the inner type
export type IncidentWorkItemPartial = Partial<
  z.infer<typeof IncidentWorkItemSchema>
>;

export const IncidentWorkItemEnhancedSchema = IncidentWorkItemBaseSchema.extend(
  {},
).brand<"vision-common:IncidentWorkItemEnhanced">();

export type IncidentWorkItemEnhanced =
  z.infer<
    typeof IncidentWorkItemEnhancedSchema
  > extends IncidentWorkItemEnhancedUnbranded
    ? z.infer<typeof IncidentWorkItemEnhancedSchema>
    : never;

/**
 *
 * Participant Work Item
 *
 */

/**
 * An ID for an Incident Work Item Participant
 */
export type IncidentWorkItemParticipantIdUnbranded = string;
export const IncidentWorkItemParticipantIdSchema =
  looseUuid().brand<"vision-common:IncidentWorkItemParticipantId">() satisfies ZodType<IncidentWorkItemParticipantIdUnbranded>;
export type IncidentWorkItemParticipantId = z.infer<
  typeof IncidentWorkItemParticipantIdSchema
>;

export const ParticipantRoleValues = [
  "owner",
  "pending-owner",
  "participant",
  "supervisor",
] as const;
export type ParticipantRole = (typeof ParticipantRoleValues)[number];
export const ParticipantRoleSchema = z.enum({
  owner: "owner",
  pendingOwner: "pending-owner",
  participant: "participant",
  supervisor: "supervisor",
} satisfies Record<string, ParticipantRole>);

///

export const ParticipantStatusValues = [
  "active",
  "pending-transfer",
  "requested-transfer",
  "rejected-transfer",
  "transferred",
  "revoked-transfer",
] as const;
export type ParticipantStatus = (typeof ParticipantStatusValues)[number];
export const ParticipantStatusSchema = z.enum({
  active: "active",
  pendingTransfer: "pending-transfer",
  requestedTransfer: "requested-transfer",
  rejectedTransfer: "rejected-transfer",
  transferred: "transferred",
  revokedTransfer: "revoked-transfer",
} satisfies Record<string, ParticipantStatus>);

/**
 * An Incident Work Item Participant
 */
export type IncidentWorkItemParticipantUnbranded = {
  id: IncidentWorkItemParticipantIdUnbranded;
  incidentWorkItemId: IncidentWorkItemParticipantIdUnbranded;
  participantId: UserIdUnbranded;
  status: ParticipantStatus;
  role: ParticipantRole;
  assignedAt: Date;
  unassignedAt?: Date | null;
  createdAt: Date;
};

export type IncidentWorkItemParticipantEnhancedUnbranded =
  IncidentWorkItemParticipantUnbranded & {
    name: string;
    email: string;
    shoulderNumber: string | null;
  };

const IncidentWorkItemParticipantSchemaBase = z.object({
  id: IncidentWorkItemParticipantIdSchema,
  incidentWorkItemId: IncidentWorkItemIdSchema,
  participantId: UserIdSchema,
  status: ParticipantStatusSchema,
  role: ParticipantRoleSchema,
  assignedAt: z.coerce.date(),
  unassignedAt: z.coerce
    .date()
    .transform((value) => (value.valueOf() === 0 ? undefined : value))
    .optional(),
  createdAt: z.coerce.date(),
});

export const IncidentWorkItemParticipantSchema =
  IncidentWorkItemParticipantSchemaBase.brand<"vision-common:IncidentWorkItemParticipant">() satisfies ZodType<IncidentWorkItemParticipantUnbranded>;

export type IncidentWorkItemParticipant = z.infer<
  typeof IncidentWorkItemParticipantSchema
>;

export type IncidentFormSectionUnbranded = {
  id: string;
  incidentFormId: string;
  data: Record<string, unknown> | null;
  updatedAt: Date;
};

export type IncidentFormUnbranded = {
  id: string;
  incidentWorkItemId: string;
  status: string;
  formType: string;
  formDefinitionVersion: string;
  createdAt: Date;
  updatedAt: Date;
  sections: IncidentFormSectionUnbranded[];
};

export type IncidentWorkItemWithContextUnbranded = {
  incidentWorkItem: IncidentWorkItemEnhancedUnbranded;
  participants: IncidentWorkItemParticipantEnhancedUnbranded[];
  emhaParticipants: EMHAParticipantUnbranded[];
  forms: IncidentFormUnbranded[];
};

export const IncidentWorkItemParticipantEnhancedSchema =
  IncidentWorkItemParticipantSchemaBase.extend({
    name: z.string().max(255),
    email: EmailSchema,
    shoulderNumber: z.string().max(255).nullable(),
  }).brand<"vision-common:IncidentWorkItemParticipant">() satisfies ZodType<IncidentWorkItemParticipantEnhancedUnbranded>;

export type IncidentWorkItemParticipantEnhanced = z.infer<
  typeof IncidentWorkItemParticipantEnhancedSchema
>;

///

export const IncidentFormStatusValues = ["active", "inactive"] as const;
export type IncidentFormStatus = (typeof IncidentFormStatusValues)[number];
export const IncidentFormStatusSchema = z.enum(IncidentFormStatusValues);

///

export const buildIncidentFormSectionSchema = (
  // Allow unknown keys on the 'generic' schema
  dataSchema = z.record(z.string(), z.unknown()).nullable(),
) =>
  z
    .object({
      id: z.string(),
      incidentFormId: IncidentFormIdSchema,
      data: dataSchema,
      updatedAt: z.coerce.date(),
    })
    .brand<"vision-common:IncidentFormSection">() satisfies ZodType<
    IncidentFormUnbranded["sections"][0]
  >;

///

export const IncidentFormTypeValues = [
  "createIncident",
  "triageIncident",
  "recordPlaceOfSafety",
  "136",
  "mentalCapacityAct",
  "preEndIncident",
  "requestPolicePresence",
  "finishForm",
  "shareIncident",
  "s136FormRejected",
] as const;
export type IncidentFormType = (typeof IncidentFormTypeValues)[number];
export const IncidentFormTypeSchema = z.enum({
  createIncident: "createIncident",
  triage: "triageIncident",
  recordPlaceOfSafety: "recordPlaceOfSafety",
  section136: "136",
  mentalCapacityAct: "mentalCapacityAct",
  // Add 5-6 etc later
  preEndIncident: "preEndIncident",
  requestPolicePresence: "requestPolicePresence",
  finishForm: "finishForm",
  shareIncident: "shareIncident",
  s136FormRejected: "s136FormRejected",
} satisfies Record<string, IncidentFormType>);

///

export const buildIncidentFormSchema = (
  sectionSchema = z.array(buildIncidentFormSectionSchema()),
) =>
  z
    .object({
      id: IncidentFormIdSchema,
      incidentWorkItemId: IncidentWorkItemIdSchema,
      status: IncidentFormStatusSchema,
      formType: IncidentFormTypeSchema,
      formDefinitionVersion: z.string(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      sections: sectionSchema,
    })
    .brand<"vision-common:IncidentForm">() satisfies ZodType<IncidentFormUnbranded>;

export const EmhaParticipantSchema = z
  .object({
    userId: z.string(),
    userName: z.string(),

    teamId: z.string(),
    teamName: z.string(),
    organisationId: z.string(),
    organisationName: z.string(),
    placeOfSafety: z.object({
      id: z.string().optional().nullable(),
      eMHACuratedPlaceOfSafetyId: z.string().optional().nullable(),
      name: z.string(),
      address: z.string(),
      postalCode: z.string(),
      odsCode: z.string().optional().nullable(),
      trustId: z.string().optional().nullable(),
    }),
    accessedAt: z.coerce.date(),
  })
  .brand<"vision-common:EmhaParticipant">() satisfies ZodType<EMHAParticipantUnbranded>;

export type EmhaParticipant = z.infer<typeof EmhaParticipantSchema>;

export const IncidentWorkItemWithContextSchema = z.object({
  incidentWorkItem: IncidentWorkItemEnhancedSchema,
  participants: z.array(IncidentWorkItemParticipantEnhancedSchema),
  forms: z.array(buildIncidentFormSchema()),
  emhaParticipants: z.array(EmhaParticipantSchema),
});

export type IncidentWorkItemWithContext = z.infer<
  typeof IncidentWorkItemWithContextSchema
>;

export type IncidentForm = IncidentWorkItemWithContext["forms"][0];
export type IncidentFormSection = IncidentForm["sections"][0];

export function getMostRecentEmhaParticipantForTeamId(
  participants: EmhaParticipant[],
  teamId: string | undefined,
): EmhaParticipant | null {
  const sortedParticipants = participants
    .filter((p) => p.teamId === teamId)
    .sort(
      (a, b) => dayjs(b.accessedAt).valueOf() - dayjs(a.accessedAt).valueOf(),
    );

  return sortedParticipants[0] ?? null;
}

/**
 *
 * Incident Work Item Snapshots
 *
 */

/**
 * An ID for an Incident Work Item Snapshot
 */
export type IncidentWorkItemSnapshotIdUnbranded = string;
export const IncidentWorkItemSnapshotIdSchema =
  looseUuid().brand<"vision-common:IncidentWorkItemSnapshotId">() satisfies ZodType<IncidentWorkItemSnapshotIdUnbranded>;
export type IncidentWorkItemSnapshotId = z.infer<
  typeof IncidentWorkItemSnapshotIdSchema
>;

/**
 * An ID for an Incident Form Snapshot
 */
export type IncidentFormSnapshotIdUnbranded = string;
export const IncidentFormSnapshotIdSchema =
  looseUuid().brand<"vision-common:IncidentFormSnapshotId">() satisfies ZodType<IncidentFormSnapshotIdUnbranded>;
export type IncidentFormSnapshotId = z.infer<
  typeof IncidentFormSnapshotIdSchema
>;

/**
 * A snapshot of an Incident Work Item at a point in time
 */
export type IncidentWorkItemSnapshotUnbranded = {
  id: IncidentWorkItemSnapshotIdUnbranded;
  incidentWorkItemId: IncidentWorkItemIdUnbranded;
  version: number;
  status: string;
  workflowVersion: string;
  shareCode: string;
  isTrainingIncident: boolean;
  anonymisationJobId: string | null;
  anonymiserVersion: string | null;
  pdfUri: string | null;
  createdAt: Date;
};

export const IncidentWorkItemSnapshotSchema = z
  .object({
    id: IncidentWorkItemSnapshotIdSchema,
    incidentWorkItemId: IncidentWorkItemIdSchema,
    version: z.number().int().positive(),
    status: IncidentWorkItemStatusSchema,
    workflowVersion: z.string().max(255),
    shareCode: z.union([z.string().regex(/^[A-Z0-9]{8}$/), looseUuid()]),
    isTrainingIncident: z.boolean(),
    anonymisationJobId: z.string().max(255).nullable(),
    anonymiserVersion: z.string().max(255).nullable(),
    pdfUri: z.string().nullable(),
    createdAt: z.coerce.date(),
  })
  .brand<"vision-common:IncidentWorkItemSnapshot">() satisfies ZodType<IncidentWorkItemSnapshotUnbranded>;

export type IncidentWorkItemSnapshot = z.infer<
  typeof IncidentWorkItemSnapshotSchema
>;

/**
 * A snapshot of an Incident Form Section at a point in time
 */
export type IncidentFormSectionSnapshotUnbranded = {
  id: string;
  incidentFormSnapshotId: IncidentFormSnapshotIdUnbranded;
  data: Record<string, unknown> | null;
  updatedAt: Date;
};

export const buildIncidentFormSectionSnapshotSchema = (
  dataSchema = z.record(z.string(), z.unknown()).nullable(),
) =>
  z
    .object({
      id: z.string(),
      incidentFormSnapshotId: IncidentFormSnapshotIdSchema,
      data: dataSchema,
      updatedAt: z.coerce.date(),
    })
    .brand<"vision-common:IncidentFormSectionSnapshot">() satisfies ZodType<IncidentFormSectionSnapshotUnbranded>;

/**
 * A snapshot of an Incident Form at a point in time
 */
export type IncidentFormSnapshotUnbranded = {
  id: IncidentFormSnapshotIdUnbranded;
  incidentWorkItemSnapshotId: IncidentWorkItemSnapshotIdUnbranded;
  originalFormId: string;
  status: string;
  formType: string;
  formDefinitionVersion: string;
  createdAt: Date;
  updatedAt: Date;
  sections: IncidentFormSectionSnapshotUnbranded[];
};

export const buildIncidentFormSnapshotSchema = (
  sectionSchema = z.array(buildIncidentFormSectionSnapshotSchema()),
) =>
  z
    .object({
      id: IncidentFormSnapshotIdSchema,
      incidentWorkItemSnapshotId: IncidentWorkItemSnapshotIdSchema,
      originalFormId: IncidentFormIdSchema,
      status: IncidentFormStatusSchema,
      formType: IncidentFormTypeSchema,
      formDefinitionVersion: z.string(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      sections: sectionSchema,
    })
    .brand<"vision-common:IncidentFormSnapshot">() satisfies ZodType<IncidentFormSnapshotUnbranded>;

/**
 * A complete snapshot with all related data
 */
export type IncidentWorkItemSnapshotWithContextUnbranded = {
  snapshot: IncidentWorkItemSnapshotUnbranded;
  forms: IncidentFormSnapshotUnbranded[];
};

export const IncidentWorkItemSnapshotWithContextSchema = z.object({
  snapshot: IncidentWorkItemSnapshotSchema,
  forms: z.array(buildIncidentFormSnapshotSchema()),
});

export type IncidentWorkItemSnapshotWithContext = z.infer<
  typeof IncidentWorkItemSnapshotWithContextSchema
>;
